import React, { FC, MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { ForgeAutocomplete, ForgeChip, ForgeChipField } from '@tylertech/forge-react';
import uniq from 'lodash/uniq';
import { IBaseListDropdownOption } from '@tylertech/forge/esm/list-dropdown';
import { View } from 'common/types/view';
import { CatalogInteractiveUser } from 'common/types/users/catalogUsers';
import { fetchJsonWithParsedError } from 'common/http';
import airbrake from 'common/airbrake';
import Spinner from '../../../Spinner';
import { I18n } from 'common/visualizations';
import { ScheduleSendValues } from '../../types/scheduleSend';
import { getCurrentDomain } from 'common/currentDomain';
import { isEmpty } from 'lodash';

const scope = 'shared.components.schedule_send_report.modal';

type Props = {
  view: View;
  scheduledSendModalVisible: boolean;
  recipients: IBaseListDropdownOption[];
  readOnly: boolean;
  updateScheduleSendValue: <K extends keyof ScheduleSendValues>(key: K, value: ScheduleSendValues[K]) => void;
  isRecipientEmptyUponSaving: boolean;
};

const RecipientsField: FC<Props> = (props) => {
  const {
    view,
    scheduledSendModalVisible,
    recipients,
    updateScheduleSendValue,
    readOnly,
    isRecipientEmptyUponSaving
  } = props;
  const [fetchedUsers, setFetchedUsers] = useState<IBaseListDropdownOption[]>([]);
  const [isFetchingUsers, setIsFetchingUsers] = useState<boolean>(true);
  const usersInputRef = useRef<HTMLInputElement>() as MutableRefObject<HTMLInputElement>;
  const [isRecipientsInvalid, setIsRecipientsInvalid] = useState(false);

  const onRemoveSelectedRecipient = useCallback(
    (recipientToRemove: IBaseListDropdownOption) => {
      updateScheduleSendValue(
        'recipients',
        recipients.filter((recipient) => recipient.value !== recipientToRemove.value)
      );

      if (recipients.length === 1) {
        setIsRecipientsInvalid(true);
      }
    },
    [recipients, updateScheduleSendValue]
  );

  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!scheduledSendModalVisible) return;

    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;
    let isMounted = true;

    // Always reset state when modal opens
    setIsFetchingUsers(true);
    setFetchedUsers([]);

    const fetchUsers = async () => {
      try {
        const params = [
          `domain=${view.domainCName || getCurrentDomain()}`,
          'include_teams=false',
          'only=site_members',
          'disabled=false',
          'future=false',
          'rights=can_collaborate'
        ].join('&');

        const url = `/api/catalog/v1/users?${params}`;
        const response: { results: CatalogInteractiveUser[] } = await fetchJsonWithParsedError(url, {
          signal
        });

        // Only update state if request wasn't aborted and component is still mounted
        if (!signal.aborted && isMounted) {
          setFetchedUsers(
            response.results
              .map((result) => {
                return {
                  label: result.email as string,
                  value: result.id
                };
              })
              .filter((result) => !recipients.some((recipient) => recipient.value === result.value))
          );
          setIsFetchingUsers(false);
        }
      } catch (error) {
        // Only handle errors if request wasn't aborted and component is still mounted
        if (!signal.aborted && isMounted) {
          airbrake.notify({ error, context: { component: 'ScheduleSendReport/RecipientsField' } });
          setIsFetchingUsers(false);
        }
      }
    };

    fetchUsers();

    // Cleanup function
    return () => {
      isMounted = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [scheduledSendModalVisible, view]);

  const hasRecipientsError = (isEmpty(recipients) && isRecipientEmptyUponSaving) || isRecipientsInvalid;

  return (
    <div data-testid="recipients-field">
      {isFetchingUsers && !readOnly ? (
        <Spinner />
      ) : (
        <ForgeAutocomplete
          mode="stateless"
          data-testid="recipients-field"
          filterOnFocus={false}
          debounce={500}
          filter={(search: string) => {
            if (search === '') {
              return [];
            }
            return fetchedUsers.filter((fetchedUser) => {
              const isAlreadySelected = recipients.some((recipient) => recipient.value === fetchedUser.value);
              const matchesSearch = fetchedUser.label.toLowerCase().includes(search.toLocaleLowerCase());

              return matchesSearch && !isAlreadySelected;
            });
          }}
          value={fetchedUsers}
          on-forge-autocomplete-select={(event: CustomEvent) => {
            setIsRecipientsInvalid(false);
            const matchedUser = fetchedUsers.find((fetchedUser) => fetchedUser.value === event.detail.value);
            if (!matchedUser) {
              return;
            }
            updateScheduleSendValue('recipients', uniq([...recipients, matchedUser]));
            usersInputRef.current.value = '';
          }}
          on-forge-chip-field-member-added={(event: CustomEvent) => {
            const matchedUser = fetchedUsers.find((fetchedUser) => fetchedUser.label === event.detail);
            if (matchedUser) {
              setIsRecipientsInvalid(false);
              updateScheduleSendValue('recipients', uniq([...recipients, matchedUser]));
            }
          }}
        >
          <ForgeChipField
            data-testid="recipients-chip-field"
            labelPosition="block-start"
            required={!readOnly}
            disabled={readOnly}
            invalid={hasRecipientsError}
          >
            <label slot="label" htmlFor="recipients-field">
              {I18n.t('recipients_label', { scope })}
            </label>
            <input
              id="recipients-field"
              data-testid="recipients-field-input"
              type="text"
              ref={usersInputRef}
              autoComplete="off"
              readOnly={readOnly}
            />
            {hasRecipientsError && <span slot="support-text">{I18n.t('required_field', { scope })}</span>}
            {recipients.map((recipient) => (
              <ForgeChip
                key={recipient.value}
                slot="member"
                type="field"
                dense
                on-forge-chip-delete={() => {
                  if (!readOnly) {
                    onRemoveSelectedRecipient(recipient);
                  }
                }}
                value={recipient.label}
                data-testid={`chip-${recipient.value}`}
                disabled={readOnly}
              >
                {recipient.label}
              </ForgeChip>
            ))}
          </ForgeChipField>
        </ForgeAutocomplete>
      )}
    </div>
  );
};

export default RecipientsField;
